import { ISaSmsPackage } from '@interfaces/sa-sms';
import { convertPrice } from '@utils/resource';
import { Button, Card, Form, InputNumber, notification, Switch, Table, Tag } from 'antd';
import { useSaSmsWrite } from 'hooks/use-sa-sms-api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SaSmsReasonModal } from './sa-sms-reason-modal';

type Values = { credits: number; priceUsd: number; isActive: boolean; sort: number };
type Props = { packages: ISaSmsPackage[]; canEdit: boolean; onChanged: () => void };

/** Credit packages CRUD (price entered in USD, stored in cents). Packages are deactivated, never deleted. */
export const SmsPackageEditor: React.FC<Props> = ({ packages, canEdit, onChanged }) => {
    const { t, i18n } = useTranslation(['sa-sms']);
    const { send } = useSaSmsWrite();
    // `null` = creating a new package.
    const [editing, setEditing] = useState<ISaSmsPackage | null | undefined>(undefined);

    const confirm = async ({ reason, priceUsd, ...rest }: Values & { reason: string }) => {
        const body = { ...rest, price: Math.round(priceUsd * 100), reason };
        if (editing) await send('put', `/settings/packages/${editing.id}`, body);
        else await send('post', '/settings/packages', body);
        notification.success({ message: t('saved') });
        setEditing(undefined);
        onChanged();
    };

    return (
        <Card
            title={t('settings.packages')}
            extra={canEdit && <Button onClick={() => setEditing(null)}>{t('actions.add')}</Button>}
        >
            <Table<ISaSmsPackage> rowKey="id" size="small" pagination={false} dataSource={packages}>
                <Table.Column<ISaSmsPackage>
                    title={t('settings.credits')}
                    align="right"
                    render={(_, p) => p.credits.toLocaleString(i18n.language)}
                />
                <Table.Column<ISaSmsPackage>
                    title={t('settings.price')}
                    align="right"
                    render={(_, p) => convertPrice(p.price)}
                />
                <Table.Column<ISaSmsPackage>
                    title={t('settings.sort')}
                    dataIndex="sort"
                    align="right"
                />
                <Table.Column<ISaSmsPackage>
                    title={t('settings.active')}
                    render={(_, p) => (
                        <Tag color={p.isActive ? 'success' : 'default'}>
                            {p.isActive ? t('common.on') : t('common.off')}
                        </Tag>
                    )}
                />
                <Table.Column<ISaSmsPackage>
                    title=""
                    render={(_, p) =>
                        canEdit && (
                            <Button size="small" onClick={() => setEditing(p)}>
                                {t('actions.edit')}
                            </Button>
                        )
                    }
                />
            </Table>
            <SaSmsReasonModal<Values>
                open={editing !== undefined}
                title={editing ? t('settings.editPackage') : t('settings.addPackage')}
                initialValues={
                    editing
                        ? {
                              credits: editing.credits,
                              priceUsd: editing.price / 100,
                              isActive: editing.isActive,
                              sort: editing.sort,
                          }
                        : { isActive: true, sort: 0 }
                }
                onCancel={() => setEditing(undefined)}
                onConfirm={confirm}
            >
                <Form.Item
                    name="credits"
                    label={t('settings.credits')}
                    rules={[{ required: true }]}
                >
                    <InputNumber min={1} max={10000000} precision={0} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                    name="priceUsd"
                    label={t('settings.priceUsd')}
                    rules={[{ required: true }]}
                >
                    <InputNumber
                        min={0.01}
                        max={1000000}
                        precision={2}
                        prefix="$"
                        style={{ width: '100%' }}
                    />
                </Form.Item>
                <Form.Item name="sort" label={t('settings.sort')}>
                    <InputNumber min={0} max={32000} precision={0} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="isActive" label={t('settings.active')} valuePropName="checked">
                    <Switch />
                </Form.Item>
            </SaSmsReasonModal>
        </Card>
    );
};
